import { ref } from "vue";
import { adminV1OrganizationsPaymentsScheduledPaymentsChargeCreate } from "@/api/admin-payments-payment-actions/admin-payments-payment-actions";
import type { ChargePaymentResponse } from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { isAxiosError } from "axios";

export type ChargeErrorType =
  | "card_expired" // 402 with error_code card_expired - the card on file has expired
  | "payment_declined" // 402 - Show error dialog with add method option
  | "validation_error" // 400 - Payment can't be charged (wrong status, cash, etc.)
  | "permission_denied" // 403 - User lacks permission
  | "not_found" // 404 - Payment doesn't exist
  | "retry_limit" // 429 - Too many retry attempts
  | "network_error" // Network/server error
  | "unknown"; // Unexpected error

export interface ChargeResult {
  success: boolean;
  data?: ChargePaymentResponse;
  errorType?: ChargeErrorType;
  errorMessage?: string;
}

export function useChargePayment() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const errorType = ref<ChargeErrorType | null>(null);
  const result = ref<ChargePaymentResponse | null>(null);

  const { currentOrganizationId } = useCurrentOrganization();

  const chargePayment = async (paymentId: string): Promise<ChargeResult> => {
    if (!currentOrganizationId.value) {
      return {
        success: false,
        errorType: "validation_error",
        errorMessage: "Organization not found",
      };
    }

    isLoading.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const response =
        await adminV1OrganizationsPaymentsScheduledPaymentsChargeCreate(
          currentOrganizationId.value,
          paymentId
        );

      result.value = response;
      return {
        success: true,
        data: response,
      };
    } catch (err: unknown) {
      let message = "Failed to process payment";
      let type: ChargeErrorType = "unknown";

      if (isAxiosError(err)) {
        const status = err.response?.status;
        // The API returns { error, error_code, details }. This used to read
        // `detail`, which the API never sends, so every message fell back to the
        // generic string below (MAI-5001).
        const data = err.response?.data;
        const detail = data?.error ?? data?.detail;
        const errorCode = data?.error_code;

        switch (status) {
          case 400:
            type = "validation_error";
            message = detail || "This payment cannot be charged";
            break;
          case 402:
            if (errorCode === "card_expired") {
              type = "card_expired";
              message = detail || "The card on file has expired";
            } else {
              type = "payment_declined";
              message = detail || "Payment was declined";
            }
            break;
          case 403:
            type = "permission_denied";
            message = "You don't have permission to charge payments";
            break;
          case 404:
            type = "not_found";
            message = "Payment not found";
            break;
          case 429:
            type = "retry_limit";
            message = "Maximum retry attempts reached. Please try again later.";
            break;
          default:
            if (!err.response) {
              type = "network_error";
              message = "Network error. Please check your connection.";
            } else {
              type = "unknown";
              message = detail || "Something went wrong. Please try again.";
            }
        }
      }

      error.value = message;
      errorType.value = type;

      return {
        success: false,
        errorType: type,
        errorMessage: message,
      };
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    errorType,
    result,
    chargePayment,
  };
}
